import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isAtLeast } from '../utils/roles';
import { apiClient, uploadFile } from '../api/client';
import { TileSummaryBlock } from '../components/TileSummaryBlock';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_PAGE,
  BG_CARD,
  FONT_HEADING,
} from '../components/legacy-ball/table-management/designTokens';

interface MarketplaceItem {
  id: string;
  name: string;
  description: string;
  price: string | null;
  caption: string | null;
  imageUrl: string | null;
  sizes: string | null;
  quantity: number | null;
  available: boolean;
  location: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

interface ItemFormData {
  name: string;
  description: string;
  price: string;
  caption: string;
  imageUrl: string;
  sizes: string;
  quantity: string;
  location: string;
  available: boolean;
  sortOrder: string;
}

const emptyForm: ItemFormData = {
  name: '',
  description: '',
  price: '',
  caption: '',
  imageUrl: '',
  sizes: '',
  quantity: '',
  location: '',
  available: true,
  sortOrder: '0',
};

// ── Custom Marketplace Card types ─────────────────────────────

interface MarketplaceTileData {
  id: string;
  cardId: string;
  label: string;
  description: string | null;
  price: string | null;
  imageUrl: string | null;
  sortOrder: number;
}

interface MarketplaceCardData {
  id: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  sortOrder: number;
  tiles: MarketplaceTileData[];
}

interface CardFormData {
  name: string;
  description: string;
  imageUrl: string;
  sortOrder: string;
}

interface TileFormData {
  label: string;
  description: string;
  price: string;
  imageUrl: string;
  sortOrder: string;
}

const emptyCardForm: CardFormData = { name: '', description: '', imageUrl: '', sortOrder: '0' };
const emptyTileForm: TileFormData = { label: '', description: '', price: '', imageUrl: '', sortOrder: '0' };

// TileSummaryBlock imported from universal component (see import above)

/** Articles Preview Popup — shows 4 random articles with AI summary on click */
function ArticlesPreviewPopup({ onClose, isOrganizer }: { onClose: () => void; isOrganizer: boolean }) {
  const [articles, setArticles] = useState<{ id: string; title: string; authorName: string; bookletSection: string | null }[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  useEffect(() => {
    apiClient<{ id: string; title: string; authorName: string; bookletSection: string | null }[]>('/weekend-booklet/random-articles')
      .then((data) => setArticles(data))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  const handleArticleClick = async (id: string) => {
    if (selectedId === id) { setSelectedId(null); setSummary(null); return; }
    setSelectedId(id);
    setSummary(null);
    setSummaryLoading(true);
    try {
      const data = await apiClient<{ summary: string }>(`/weekend-booklet/submissions/${id}/summary`);
      setSummary(data.summary);
    } catch {
      setSummary('Unable to generate summary.');
    } finally {
      setSummaryLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '1rem' }} onClick={onClose} role="dialog" aria-modal="true">
      <div style={{ backgroundColor: '#fff', borderRadius: '12px', maxWidth: '420px', width: '100%', padding: '1.25rem', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', maxHeight: '80vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1a2e23' }}>📰 Featured Articles</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#666' }} aria-label="Close">✕</button>
        </div>
        <TileSummaryBlock sectionKey="articles" isOrganizer={isOrganizer} contextForAI="Articles submitted for the KDU Georgia 2026 Weekend Booklet, including stories, reports, and cultural pieces from the reunion community." />
        {loading ? (
          <p style={{ textAlign: 'center', color: '#666', padding: '1rem' }}>Loading articles...</p>
        ) : articles.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#666', padding: '1rem' }}>No articles available yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {articles.map((a) => (
              <div key={a.id}>
                <button
                  onClick={() => handleArticleClick(a.id)}
                  style={{ width: '100%', textAlign: 'left', padding: '0.75rem', backgroundColor: selectedId === a.id ? '#e8f5e9' : '#f9fafb', border: `1px solid ${selectedId === a.id ? '#1a6b3c' : '#e5e7eb'}`, borderRadius: '6px', cursor: 'pointer', transition: 'all 0.15s' }}
                >
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1a2e23' }}>{a.title}</div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '2px' }}>by {a.authorName}{a.bookletSection ? ` · ${a.bookletSection}` : ''}</div>
                </button>
                {selectedId === a.id && (
                  <div style={{ padding: '0.6rem 0.75rem', fontSize: '0.82rem', color: '#333', lineHeight: 1.5, backgroundColor: '#f0f7f2', borderRadius: '0 0 6px 6px', borderTop: 'none' }}>
                    {summaryLoading ? <span style={{ color: '#6b7280', fontStyle: 'italic' }}>Generating summary...</span> : summary}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
          <button onClick={onClose} style={{ padding: '0.5rem 1.5rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/** Advertisers Preview Popup — 2-column list with lightbox for full-page ads */
function AdvertisersPreviewPopup({ onClose, isOrganizer }: { onClose: () => void; isOrganizer: boolean }) {
  const { accessToken } = useAuth();
  const [advertisers, setAdvertisers] = useState<{ id: string; title: string; authorName: string; attachmentUrl: string | null }[]>([]);
  const [loading, setLoading] = useState(true);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [blobUrls, setBlobUrls] = useState<Record<string, string>>({});
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

  useEffect(() => {
    (async () => {
      try {
        const ed = await apiClient<{ id: string }>('/weekend-booklet/editions/active');
        if (!ed?.id) { setLoading(false); return; }
        const ads = await apiClient<{ id: string; title: string; authorName: string; attachmentUrl: string | null }[]>(
          `/weekend-booklet/submissions?editionId=${ed.id}&sectionCategory=Advertisers`
        );
        setAdvertisers(ads);

        // Pre-fetch images as authenticated blobs
        const urlMap: Record<string, string> = {};
        await Promise.all(
          ads.filter((a) => a.attachmentUrl).map(async (a) => {
            try {
              const resp = await window.fetch(`${API_URL}/weekend-booklet/${a.attachmentUrl}`, {
                headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
              });
              if (resp.ok) {
                const blob = await resp.blob();
                urlMap[a.id] = URL.createObjectURL(blob);
              }
            } catch {}
          }),
        );
        setBlobUrls(urlMap);
      } catch { /* silent */ }
      setLoading(false);
    })();
  }, [accessToken, API_URL]);

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '1rem' }} onClick={onClose} role="dialog" aria-modal="true">
        <div style={{ backgroundColor: '#fff', borderRadius: '12px', maxWidth: '500px', width: '100%', padding: '1.25rem', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', maxHeight: '80vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1a2e23' }}>📣 Ad Space Purchasers</h3>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#666' }} aria-label="Close">✕</button>
          </div>
          <TileSummaryBlock sectionKey="advertisers" isOrganizer={isOrganizer} contextForAI="Advertisers and sponsors who purchased space in the KDU Georgia 2026 Weekend Booklet to support the reunion." />
          {loading ? (
            <p style={{ textAlign: 'center', color: '#666', padding: '1rem' }}>Loading advertisers...</p>
          ) : advertisers.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#666', padding: '1rem' }}>No advertisers yet.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              {advertisers.map((ad) => (
                <button
                  key={ad.id}
                  onClick={() => blobUrls[ad.id] ? setLightboxUrl(blobUrls[ad.id]) : undefined}
                  style={{
                    textAlign: 'left',
                    padding: '0.6rem 0.75rem',
                    backgroundColor: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    cursor: blobUrls[ad.id] ? 'pointer' : 'default',
                    opacity: blobUrls[ad.id] ? 1 : 0.7,
                  }}
                >
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1a2e23', lineHeight: 1.3 }}>{ad.title}</div>
                  <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '2px' }}>{ad.authorName}</div>
                </button>
              ))}
            </div>
          )}
          <div style={{ marginTop: '1rem', textAlign: 'center' }}>
            <button onClick={onClose} style={{ padding: '0.5rem 1.5rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Full-page ad lightbox */}
      {lightboxUrl && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001, padding: '1rem' }}
          onClick={() => setLightboxUrl(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Full page ad"
        >
          <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
            <img src={lightboxUrl} alt="Full page ad" style={{ maxWidth: '100%', maxHeight: '85vh', borderRadius: '4px', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }} />
            <button
              onClick={() => setLightboxUrl(null)}
              style={{ position: 'absolute', top: '-12px', right: '-12px', width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#fff', border: 'none', fontSize: '1.1rem', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              aria-label="Close ad preview"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/** Memorial Tiles Preview — shows 4 random inscriptions with unique memorial backgrounds */
const MEMORIAL_THEMES = [
  { bg: 'linear-gradient(135deg, #1a1a2e, #16213e)', color: '#e8d5b7', border: '#3a3a5e' },
  { bg: 'linear-gradient(135deg, #2d1b4e, #1a1a2e)', color: '#d4c5f9', border: '#4a3a6e' },
  { bg: 'linear-gradient(135deg, #0f3443, #1a3a4a)', color: '#b8dce8', border: '#2a5a6a' },
  { bg: 'linear-gradient(135deg, #3d1f1f, #2a1515)', color: '#f0d4c8', border: '#5a3a3a' },
];

/** Congrats Preview Popup — shows 6 random congratulatory messages with AI summary on click */
function CongratsPreviewPopup({ onClose, isOrganizer }: { onClose: () => void; isOrganizer: boolean }) {
  const [messages, setMessages] = useState<{ id: string; title: string; authorName: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  useEffect(() => {
    apiClient<{ id: string; title: string; authorName: string }[]>('/weekend-booklet/random-congrats')
      .then((data) => setMessages(data))
      .catch(() => setMessages([]))
      .finally(() => setLoading(false));
  }, []);

  const handleMessageClick = async (id: string) => {
    if (selectedId === id) { setSelectedId(null); setSummary(null); return; }
    setSelectedId(id);
    setSummary(null);
    setSummaryLoading(true);
    try {
      const data = await apiClient<{ summary: string }>(`/weekend-booklet/submissions/${id}/summary`);
      setSummary(data.summary);
    } catch {
      setSummary('Unable to generate summary.');
    } finally {
      setSummaryLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '1rem' }} onClick={onClose} role="dialog" aria-modal="true">
      <div style={{ backgroundColor: '#fff', borderRadius: '12px', maxWidth: '420px', width: '100%', padding: '1.25rem', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', maxHeight: '80vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1a2e23' }}>🎉 Congratulatory Messages</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#666' }} aria-label="Close">✕</button>
        </div>
        <TileSummaryBlock sectionKey="congrats" isOrganizer={isOrganizer} contextForAI="Congratulatory messages submitted for the KDU Georgia 2026 Weekend Booklet from chapters, families, and organizations celebrating the reunion." />
        {loading ? (
          <p style={{ textAlign: 'center', color: '#666', padding: '1rem' }}>Loading messages...</p>
        ) : messages.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#666', padding: '1rem' }}>No approved congratulatory messages yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {messages.map((m) => (
              <div key={m.id}>
                <button
                  onClick={() => handleMessageClick(m.id)}
                  style={{ width: '100%', textAlign: 'left', padding: '0.75rem', backgroundColor: selectedId === m.id ? '#e8f5e9' : '#f9fafb', border: `1px solid ${selectedId === m.id ? '#1a6b3c' : '#e5e7eb'}`, borderRadius: '6px', cursor: 'pointer', transition: 'all 0.15s' }}
                >
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1a2e23' }}>{m.title}</div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '2px' }}>by {m.authorName}</div>
                </button>
                {selectedId === m.id && (
                  <div style={{ padding: '0.6rem 0.75rem', marginTop: '4px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px' }}>
                    {summaryLoading ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>Generating AI summary...</span>
                        <div style={{ position: 'relative', width: '100%', height: '6px', backgroundColor: '#e5e7eb', borderRadius: '3px', overflow: 'hidden' }}>
                          <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                            <span style={{ position: 'absolute', fontSize: '10px', lineHeight: '6px', animation: 'candleSlide 2s ease-in-out infinite' }}>🕯️</span>
                          </div>
                        </div>
                        <style>{`@keyframes candleSlide { 0% { left: 0%; } 50% { left: 90%; } 100% { left: 0%; } }`}</style>
                      </div>
                    ) : (
                      <p style={{ fontSize: '0.82rem', color: '#1a2e23', lineHeight: 1.5, margin: 0 }}>{summary}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
          <button onClick={onClose} style={{ padding: '0.5rem 1.5rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/** Sponsors Preview Popup — shows sponsor ad submissions from BookletSubmission (SponsorAds) */
function SponsorsPreviewPopup({ onClose }: { onClose: () => void }) {
  const { accessToken } = useAuth();
  const [sponsors, setSponsors] = useState<{ id: string; title: string; authorName: string; attachmentUrl: string | null; adSize: string | null }[]>([]);
  const [loading, setLoading] = useState(true);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [lightboxIsPdf, setLightboxIsPdf] = useState(false);
  const [blobUrls, setBlobUrls] = useState<Record<string, string>>({});
  const [blobTypes, setBlobTypes] = useState<Record<string, string>>({});
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

  useEffect(() => {
    (async () => {
      try {
        const ed = await apiClient<{ id: string }>('/weekend-booklet/editions/active');
        if (!ed?.id) { setLoading(false); return; }
        const subs = await apiClient<{ id: string; title: string; authorName: string; attachmentUrl: string | null; adSize: string | null }[]>(
          `/weekend-booklet/submissions?editionId=${ed.id}&sectionCategory=SponsorAds`
        );
        setSponsors(subs);

        // Pre-fetch attachments as authenticated blobs
        const urlMap: Record<string, string> = {};
        const typeMap: Record<string, string> = {};
        await Promise.all(
          subs.filter((s) => s.attachmentUrl).map(async (s) => {
            try {
              const resp = await window.fetch(`${API_URL}/weekend-booklet/${s.attachmentUrl}`, {
                headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
              });
              if (resp.ok) {
                const contentType = resp.headers.get('content-type') || '';
                const blob = await resp.blob();
                urlMap[s.id] = URL.createObjectURL(blob);
                typeMap[s.id] = contentType.includes('pdf') ? 'pdf' : 'image';
              }
            } catch {}
          }),
        );
        setBlobUrls(urlMap);
        setBlobTypes(typeMap);
      } catch { /* silent */ }
      setLoading(false);
    })();
  }, [accessToken, API_URL]);

  const handleThumbnailClick = (id: string) => {
    if (!blobUrls[id]) return;
    setLightboxUrl(blobUrls[id]);
    setLightboxIsPdf(blobTypes[id] === 'pdf');
  };

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '1rem' }} onClick={onClose} role="dialog" aria-modal="true">
        <div style={{ backgroundColor: '#fff', borderRadius: '12px', maxWidth: '500px', width: '100%', padding: '1.25rem', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', maxHeight: '80vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1a2e23' }}>🤝 Sponsors</h3>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#666' }} aria-label="Close">✕</button>
          </div>

          {loading ? (
            <p style={{ textAlign: 'center', color: '#666', padding: '1rem' }}>Loading sponsors...</p>
          ) : sponsors.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#999', fontStyle: 'italic' }}>No sponsor ads yet.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              {sponsors.map((s) => (
                <button
                  key={s.id}
                  onClick={() => handleThumbnailClick(s.id)}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '0.5rem',
                    backgroundColor: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    cursor: blobUrls[s.id] ? 'pointer' : 'default',
                    opacity: blobUrls[s.id] ? 1 : 0.7,
                    transition: 'box-shadow 0.15s',
                  }}
                >
                  {/* Thumbnail area — contain full image */}
                  <div style={{ width: '100%', aspectRatio: '4/3', borderRadius: '4px', overflow: 'hidden', backgroundColor: '#e5e7eb', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {blobUrls[s.id] ? (
                      blobTypes[s.id] === 'pdf' ? (
                        <iframe
                          src={blobUrls[s.id]}
                          title={s.title}
                          style={{ width: '200%', height: '200%', border: 'none', pointerEvents: 'none', transform: 'scale(0.5)', transformOrigin: 'top left' }}
                        />
                      ) : (
                        <img src={blobUrls[s.id]} alt={s.title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      )
                    ) : (
                      <span style={{ fontSize: '1.5rem' }}>🤝</span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a2e23', lineHeight: 1.3, textAlign: 'center' }}>{s.title}</div>
                  <div style={{ fontSize: '0.65rem', color: '#6b7280', marginTop: '2px' }}>{s.authorName}</div>
                  {s.adSize && <div style={{ fontSize: '0.6rem', color: '#9ca3af', marginTop: '1px' }}>{s.adSize}</div>}
                </button>
              ))}
            </div>
          )}
          <div style={{ marginTop: '1rem', textAlign: 'center' }}>
            <button onClick={onClose} style={{ padding: '0.5rem 1.5rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Full-page sponsor ad lightbox */}
      {lightboxUrl && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001, padding: '1rem' }}
          onClick={() => { setLightboxUrl(null); setLightboxIsPdf(false); }}
          role="dialog"
          aria-modal="true"
          aria-label="Full page sponsor ad"
        >
          <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh', width: lightboxIsPdf ? '80vw' : 'auto', height: lightboxIsPdf ? '85vh' : 'auto' }} onClick={(e) => e.stopPropagation()}>
            {lightboxIsPdf ? (
              <iframe src={lightboxUrl} title="Sponsor Ad PDF" style={{ width: '100%', height: '85vh', border: 'none', borderRadius: '4px', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }} />
            ) : (
              <img src={lightboxUrl} alt="Full page sponsor ad" style={{ maxWidth: '100%', maxHeight: '85vh', borderRadius: '4px', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }} />
            )}
            <button
              onClick={() => { setLightboxUrl(null); setLightboxIsPdf(false); }}
              style={{ position: 'absolute', top: '-12px', right: '-12px', width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#fff', border: 'none', fontSize: '1.1rem', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              aria-label="Close sponsor ad preview"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function MemorialTilesPreviewPopup({ onClose, isOrganizer }: { onClose: () => void; isOrganizer: boolean }) {
  const { accessToken } = useAuth();
  const [tiles, setTiles] = useState<{ id: string; title: string; authorName: string; inMemoryOf: string | null; attachmentUrl: string | null }[]>([]);
  const [loading, setLoading] = useState(true);
  const [blobUrls, setBlobUrls] = useState<Record<string, string>>({});
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

  useEffect(() => {
    (async () => {
      try {
        const ed = await apiClient<{ id: string }>('/weekend-booklet/editions/active');
        if (!ed?.id) { setLoading(false); return; }
        const subs = await apiClient<{ id: string; title: string; authorName: string; inMemoryOf: string | null; attachmentUrl: string | null }[]>(
          `/weekend-booklet/submissions?editionId=${ed.id}&sectionCategory=TileSpacePurchasers`
        );
        // Shuffle and take up to 6
        const shuffled = subs.sort(() => Math.random() - 0.5).slice(0, 6);
        setTiles(shuffled);

        // Pre-fetch images as authenticated blobs
        const urlMap: Record<string, string> = {};
        await Promise.all(
          shuffled.filter((s) => s.attachmentUrl).map(async (s) => {
            try {
              const resp = await window.fetch(`${API_URL}/weekend-booklet/${s.attachmentUrl}`, {
                headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
              });
              if (resp.ok) {
                const blob = await resp.blob();
                urlMap[s.id] = URL.createObjectURL(blob);
              }
            } catch {}
          }),
        );
        setBlobUrls(urlMap);
      } catch { /* silent */ }
      setLoading(false);
    })();
  }, [accessToken, API_URL]);

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '1rem' }} onClick={onClose} role="dialog" aria-modal="true">
        <div style={{ backgroundColor: '#1a1a2e', borderRadius: '12px', maxWidth: '500px', width: '100%', padding: '1.25rem', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', maxHeight: '85vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#e8d5b7' }}>🕊️ Memorial Tiles</h3>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#aaa' }} aria-label="Close">✕</button>
          </div>
          <TileSummaryBlock sectionKey="memorial" isOrganizer={isOrganizer} contextForAI="Memorial tiles honoring departed KDU family members, with tributes purchased by loved ones for the Weekend Booklet." />
          {loading ? (
            <p style={{ textAlign: 'center', color: '#aaa', padding: '1rem' }}>Loading...</p>
          ) : tiles.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#aaa', padding: '1rem' }}>No memorial tiles available.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {tiles.map((tile, idx) => {
                const theme = MEMORIAL_THEMES[idx % MEMORIAL_THEMES.length];
                const hasBlobImage = !!blobUrls[tile.id];
                return (
                  <div
                    key={tile.id}
                    onClick={() => hasBlobImage ? setLightboxUrl(blobUrls[tile.id]) : undefined}
                    style={{ background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: '8px', overflow: 'hidden', cursor: hasBlobImage ? 'pointer' : 'default', display: 'flex', flexDirection: 'column' }}
                  >
                    {hasBlobImage ? (
                      <img src={blobUrls[tile.id]} alt={tile.inMemoryOf || tile.title} style={{ width: '100%', height: '120px', objectFit: 'contain', backgroundColor: 'rgba(0,0,0,0.3)' }} />
                    ) : (
                      <div style={{ width: '100%', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontSize: '1.5rem' }}>🕊️</span>
                      </div>
                    )}
                    <div style={{ padding: '0.5rem 0.6rem', textAlign: 'center' }}>
                      {tile.inMemoryOf && (
                        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: theme.color, fontStyle: 'italic', lineHeight: 1.3 }}>
                          In memory of {tile.inMemoryOf}
                        </div>
                      )}
                      <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.6)', marginTop: '3px' }}>by {tile.authorName}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ marginTop: '1rem', textAlign: 'center' }}>
            <button onClick={onClose} style={{ padding: '0.5rem 1.5rem', backgroundColor: 'rgba(255,255,255,0.1)', color: '#e8d5b7', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Memorial tile lightbox */}
      {lightboxUrl && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001, padding: '1rem' }}
          onClick={() => setLightboxUrl(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Memorial tile full view"
        >
          <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
            <img src={lightboxUrl} alt="Memorial tile" style={{ maxWidth: '100%', maxHeight: '85vh', borderRadius: '4px', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }} />
            <button
              onClick={() => setLightboxUrl(null)}
              style={{ position: 'absolute', top: '-12px', right: '-12px', width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#fff', border: 'none', fontSize: '1.1rem', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              aria-label="Close memorial tile preview"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export function MarketplacePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isOrganizer = user && isAtLeast(user.role, 'Organizer');

  const [items, setItems] = useState<MarketplaceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<MarketplaceItem | null>(null);
  const [formData, setFormData] = useState<ItemFormData>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [showCoverPopup, setShowCoverPopup] = useState(false);
  const [showArticlesPopup, setShowArticlesPopup] = useState(false);
  const [showAdvertisersPopup, setShowAdvertisersPopup] = useState(false);
  const [showMemorialPopup, setShowMemorialPopup] = useState(false);
  const [showSponsorsPopup, setShowSponsorsPopup] = useState(false);
  const [showCongratsPopup, setShowCongratsPopup] = useState(false);

  // Custom cards state
  const [cards, setCards] = useState<MarketplaceCardData[]>([]);
  const [selectedCard, setSelectedCard] = useState<MarketplaceCardData | null>(null);
  const [showCardModal, setShowCardModal] = useState(false);
  const [editingCard, setEditingCard] = useState<MarketplaceCardData | null>(null);
  const [cardForm, setCardForm] = useState<CardFormData>(emptyCardForm);
  const [showTileModal, setShowTileModal] = useState(false);
  const [editingTile, setEditingTile] = useState<MarketplaceTileData | null>(null);
  const [tileForm, setTileForm] = useState<TileFormData>(emptyTileForm);
  const [cardSubmitting, setCardSubmitting] = useState(false);

  const fetchCards = async () => {
    try {
      const data = await apiClient<MarketplaceCardData[]>('/marketplace/cards');
      setCards(data);
    } catch { /* silent */ }
  };

  const fetchItems = async () => {
    try {
      setError(null);
      const data = await apiClient<MarketplaceItem[]>('/marketplace');
      setItems(data);
    } catch {
      setError('Unable to load marketplace items.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    fetchCards();
  }, []);

  const openAddForm = () => {
    setEditingItem(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEditForm = (item: MarketplaceItem) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description,
      price: item.price != null ? String(item.price) : '',
      caption: item.caption || '',
      imageUrl: item.imageUrl || '',
      sizes: item.sizes || '',
      quantity: item.quantity != null ? String(item.quantity) : '',
      location: item.location,
      available: item.available,
      sortOrder: String(item.sortOrder),
    });
    setShowModal(true);
  };

  const handleDelete = async (item: MarketplaceItem) => {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
    try {
      await apiClient(`/marketplace/${item.id}`, { method: 'DELETE' });
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    } catch {
      alert('Failed to delete item.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload: Record<string, any> = {
        name: formData.name,
        description: formData.description,
        imageUrl: formData.imageUrl || undefined,
        location: formData.location,
        available: formData.available,
        sortOrder: parseInt(formData.sortOrder, 10) || 0,
      };

      // Price is optional — send null if empty
      if (formData.price.trim()) {
        payload.price = parseFloat(formData.price);
      } else {
        payload.price = null;
      }

      // Caption (e.g. "FREE") — send null if empty
      payload.caption = formData.caption.trim() || null;

      // Sizes and quantity are optional
      payload.sizes = formData.sizes.trim() || null;
      if (formData.quantity.trim()) {
        payload.quantity = parseInt(formData.quantity, 10);
      } else {
        payload.quantity = null;
      }

      if (editingItem) {
        await apiClient(`/marketplace/${editingItem.id}`, { method: 'PATCH', body: payload });
      } else {
        await apiClient('/marketplace', { method: 'POST', body: payload });
      }
      setShowModal(false);
      setLoading(true);
      await fetchItems();
    } catch {
      alert('Failed to save item.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Card CRUD handlers ─────────────────────────────────────

  const openAddCard = () => {
    setEditingCard(null);
    setCardForm(emptyCardForm);
    setShowCardModal(true);
  };

  const openEditCard = (card: MarketplaceCardData) => {
    setEditingCard(card);
    setCardForm({
      name: card.name,
      description: card.description || '',
      imageUrl: card.imageUrl || '',
      sortOrder: String(card.sortOrder),
    });
    setShowCardModal(true);
  };

  const handleCardSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCardSubmitting(true);
    try {
      const payload = {
        name: cardForm.name,
        description: cardForm.description || undefined,
        imageUrl: cardForm.imageUrl || undefined,
        sortOrder: parseInt(cardForm.sortOrder) || 0,
      };
      if (editingCard) {
        await apiClient(`/marketplace/cards/${editingCard.id}`, { method: 'PATCH', body: payload });
      } else {
        await apiClient('/marketplace/cards', { method: 'POST', body: payload });
      }
      setShowCardModal(false);
      fetchCards();
    } catch {
      alert('Failed to save card.');
    } finally {
      setCardSubmitting(false);
    }
  };

  const handleDeleteCard = async (card: MarketplaceCardData) => {
    if (!window.confirm(`Delete "${card.name}" and all its tiles? This cannot be undone.`)) return;
    try {
      await apiClient(`/marketplace/cards/${card.id}`, { method: 'DELETE' });
      setCards((prev) => prev.filter((c) => c.id !== card.id));
      if (selectedCard?.id === card.id) setSelectedCard(null);
    } catch {
      alert('Failed to delete card.');
    }
  };

  // ── Tile CRUD handlers ──────────────────────────────────────

  const openAddTile = () => {
    setEditingTile(null);
    setTileForm(emptyTileForm);
    setShowTileModal(true);
  };

  const openEditTile = (tile: MarketplaceTileData) => {
    setEditingTile(tile);
    setTileForm({
      label: tile.label,
      description: tile.description || '',
      price: tile.price || '',
      imageUrl: tile.imageUrl || '',
      sortOrder: String(tile.sortOrder),
    });
    setShowTileModal(true);
  };

  const handleTileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCard) return;
    setCardSubmitting(true);
    try {
      const payload = {
        label: tileForm.label,
        description: tileForm.description || undefined,
        price: tileForm.price || undefined,
        imageUrl: tileForm.imageUrl || undefined,
        sortOrder: parseInt(tileForm.sortOrder) || 0,
      };
      if (editingTile) {
        await apiClient(`/marketplace/tiles/${editingTile.id}`, { method: 'PATCH', body: payload });
      } else {
        await apiClient(`/marketplace/cards/${selectedCard.id}/tiles`, { method: 'POST', body: payload });
      }
      setShowTileModal(false);
      // Refresh the selected card
      const updated = await apiClient<MarketplaceCardData>(`/marketplace/cards/${selectedCard.id}`);
      setSelectedCard(updated);
      fetchCards();
    } catch {
      alert('Failed to save tile.');
    } finally {
      setCardSubmitting(false);
    }
  };

  const handleDeleteTile = async (tile: MarketplaceTileData) => {
    if (!window.confirm(`Delete tile "${tile.label}"?`)) return;
    try {
      await apiClient(`/marketplace/tiles/${tile.id}`, { method: 'DELETE' });
      if (selectedCard) {
        const updated = await apiClient<MarketplaceCardData>(`/marketplace/cards/${selectedCard.id}`);
        setSelectedCard(updated);
        fetchCards();
      }
    } catch {
      alert('Failed to delete tile.');
    }
  };

  const formatPrice = (price: string | number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(Number(price));
  };

  // --- Styles ---
  const pageStyle: React.CSSProperties = {
    padding: '2rem',
    minHeight: '100%',
  };

  const headerStyle: React.CSSProperties = {
    marginBottom: '1.5rem',
  };

  const titleStyle: React.CSSProperties = {
    fontFamily: FONT_HEADING,
    fontSize: '1.75rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: 0,
  };

  const subtitleStyle: React.CSSProperties = {
    color: TEXT_MUTED,
    fontSize: '0.95rem',
    marginTop: '0.25rem',
  };

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '1.25rem',
    padding: '1rem 0',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: BG_CARD,
    borderRadius: '8px',
    border: `1px solid ${BORDER_LIGHT}`,
    padding: '1.25rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    position: 'relative',
  };

  const cardImageStyle: React.CSSProperties = {
    width: '100%',
    height: 'auto',
    maxHeight: '300px',
    objectFit: 'contain',
    borderRadius: '6px',
    marginBottom: '0.5rem',
    backgroundColor: '#f9fafb',
  };

  const cardNameStyle: React.CSSProperties = {
    fontFamily: FONT_HEADING,
    fontSize: '1.1rem',
    fontWeight: 600,
    color: TEXT_DARK,
    margin: 0,
  };

  const cardDescStyle: React.CSSProperties = {
    color: TEXT_MUTED,
    fontSize: '0.85rem',
    lineHeight: 1.4,
    margin: 0,
  };

  const priceStyle: React.CSSProperties = {
    fontWeight: 700,
    fontSize: '1.1rem',
    color: PRIMARY,
  };

  const metaStyle: React.CSSProperties = {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
  };

  const soldOutBadge: React.CSSProperties = {
    position: 'absolute',
    top: '12px',
    right: '12px',
    backgroundColor: '#d32f2f',
    color: '#fff',
    fontSize: '0.7rem',
    fontWeight: 700,
    padding: '3px 8px',
    borderRadius: '4px',
    textTransform: 'uppercase',
  };

  const iconBtn: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px 6px',
    fontSize: '0.9rem',
    borderRadius: '4px',
    lineHeight: 1,
    transition: 'background-color 0.15s',
  };

  const cardActions: React.CSSProperties = {
    display: 'flex',
    gap: '4px',
    marginTop: 'auto',
    paddingTop: '0.5rem',
    borderTop: `1px solid ${BORDER_LIGHT}`,
  };

  const addBtnStyle: React.CSSProperties = {
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    padding: '0.5rem 1.25rem',
    borderRadius: '6px',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '0.9rem',
  };

  const errorBannerStyle: React.CSSProperties = {
    backgroundColor: '#fef2f2',
    color: '#991b1b',
    border: '1px solid #fecaca',
    padding: '0.75rem 1rem',
    borderRadius: '6px',
    marginBottom: '1rem',
  };

  const emptyStateStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '3rem 1rem',
    color: TEXT_MUTED,
    fontSize: '1rem',
  };

  const bookletSubCard: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    padding: '12px 8px',
    backgroundColor: '#f9fafb',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  };

  const bookletSubLabel: React.CSSProperties = {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: TEXT_DARK,
    textAlign: 'center',
  };

  // Modal styles
  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: BG_CARD,
    borderRadius: '10px',
    padding: '2rem',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflowY: 'auto',
  };

  const fieldStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: '0.75rem',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: TEXT_DARK,
  };

  const inputStyle: React.CSSProperties = {
    padding: '0.5rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    fontSize: '0.9rem',
  };

  const textareaStyle: React.CSSProperties = {
    ...inputStyle,
    minHeight: '80px',
    resize: 'vertical',
  };

  const checkboxRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '0.75rem',
  };

  const modalFooter: React.CSSProperties = {
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'flex-end',
    marginTop: '1rem',
  };

  if (loading) {
    return (
      <div style={pageStyle}>
        <p style={{ color: TEXT_MUTED }}>Loading marketplace…</p>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={{ ...headerStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={titleStyle}>🛍️ Weekend Marketplace</h1>
          <p style={subtitleStyle}>Items available for purchase during the reunion weekend</p>
        </div>
        {isOrganizer && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button style={addBtnStyle} onClick={openAddCard}>+ Add Card</button>
            <button style={addBtnStyle} onClick={openAddForm}>+ Add Item</button>
          </div>
        )}
      </div>

      {error && <div style={errorBannerStyle}>{error}</div>}

      {!error && items.length === 0 && (
        <div>
          {/* Weekend Booklet card with sub-sections */}
          <div style={{ ...cardStyle, borderColor: PRIMARY, borderWidth: '2px', marginBottom: '1rem' }}>
            <h3 style={{ ...cardNameStyle, textAlign: 'center' as const }}>📖 Preview Weekend Booklet</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginTop: '0.75rem' }}>
              <button style={{ ...bookletSubCard, padding: 0, overflow: 'hidden', position: 'relative' as const }} onClick={() => setShowCoverPopup(true)} aria-label="View Cover">
                <img src="/images/booklet-cover.jpg" alt="Booklet Cover" style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute' as const, inset: 0 }} />
                <span style={{ ...bookletSubLabel, position: 'relative' as const, zIndex: 1, backgroundColor: 'rgba(0,0,0,0.5)', color: '#fff', padding: '2px 6px', borderRadius: '3px', marginTop: 'auto', marginBottom: '6px' }}>Cover</span>
              </button>
              <button style={bookletSubCard} onClick={() => setShowArticlesPopup(true)} aria-label="Articles">
                <span style={{ fontSize: '1.5rem' }}>📰</span>
                <span style={bookletSubLabel}>Articles</span>
              </button>
              <button style={bookletSubCard} onClick={() => setShowAdvertisersPopup(true)} aria-label="Advertisers">
                <span style={{ fontSize: '1.5rem' }}>📣</span>
                <span style={bookletSubLabel}>Advertisers</span>
              </button>
              <button style={bookletSubCard} onClick={() => setShowMemorialPopup(true)} aria-label="Memorial Tiles">
                <span style={{ fontSize: '1.5rem' }}>🕊️</span>
                <span style={bookletSubLabel}>Memorial Tiles</span>
              </button>
              <button style={bookletSubCard} onClick={() => setShowCongratsPopup(true)} aria-label="Congratulatory Messages">
                <span style={{ fontSize: '1.5rem' }}>🎉</span>
                <span style={bookletSubLabel}>Congrats</span>
              </button>
              <button style={bookletSubCard} onClick={() => setShowSponsorsPopup(true)} aria-label="Sponsors">
                <span style={{ fontSize: '1.5rem' }}>🤝</span>
                <span style={bookletSubLabel}>Sponsors</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {(items.length > 0 || cards.length > 0) && (
        <div style={gridStyle}>
          {/* Weekend Booklet card with sub-sections */}
          <div style={{ ...cardStyle, borderColor: PRIMARY, borderWidth: '2px' }}>
            <h3 style={{ ...cardNameStyle, textAlign: 'center' as const }}>📖 Preview Weekend Booklet</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginTop: '0.75rem' }}>
              <button style={{ ...bookletSubCard, padding: 0, overflow: 'hidden', position: 'relative' as const }} onClick={() => setShowCoverPopup(true)} aria-label="View Cover">
                <img src="/images/booklet-cover.jpg" alt="Booklet Cover" style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute' as const, inset: 0 }} />
                <span style={{ ...bookletSubLabel, position: 'relative' as const, zIndex: 1, backgroundColor: 'rgba(0,0,0,0.5)', color: '#fff', padding: '2px 6px', borderRadius: '3px', marginTop: 'auto', marginBottom: '6px' }}>Cover</span>
              </button>
              <button style={bookletSubCard} onClick={() => setShowArticlesPopup(true)} aria-label="Articles">
                <span style={{ fontSize: '1.5rem' }}>📰</span>
                <span style={bookletSubLabel}>Articles</span>
              </button>
              <button style={bookletSubCard} onClick={() => setShowAdvertisersPopup(true)} aria-label="Advertisers">
                <span style={{ fontSize: '1.5rem' }}>📣</span>
                <span style={bookletSubLabel}>Advertisers</span>
              </button>
              <button style={bookletSubCard} onClick={() => setShowMemorialPopup(true)} aria-label="Memorial Tiles">
                <span style={{ fontSize: '1.5rem' }}>🕊️</span>
                <span style={bookletSubLabel}>Memorial Tiles</span>
              </button>
              <button style={bookletSubCard} onClick={() => setShowCongratsPopup(true)} aria-label="Congratulatory Messages">
                <span style={{ fontSize: '1.5rem' }}>🎉</span>
                <span style={bookletSubLabel}>Congrats</span>
              </button>
              <button style={bookletSubCard} onClick={() => setShowSponsorsPopup(true)} aria-label="Sponsors">
                <span style={{ fontSize: '1.5rem' }}>🤝</span>
                <span style={bookletSubLabel}>Sponsors</span>
              </button>
            </div>
          </div>
          {/* Vendor Showcase Card */}
          <Link
            to="/vendor-showcase"
            style={{ ...cardStyle, borderColor: '#92400e', borderWidth: '2px', cursor: 'pointer', textDecoration: 'none' }}
            aria-label="Browse Vendor Showcase"
          >
            <h3 style={{ ...cardNameStyle, textAlign: 'center' as const }}>🏪 Vendor Showcase</h3>
            <p style={{ ...cardDescStyle, textAlign: 'center' as const }}>Browse food, merchandise & services from reunion weekend vendors</p>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '0.5rem' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#92400e', backgroundColor: '#fef3c7', padding: '6px 14px', borderRadius: '16px' }}>
                Tap to explore →
              </span>
            </div>
          </Link>
          {items.map((item) => (
            <div key={item.id} style={cardStyle}>
              {!item.available && <span style={soldOutBadge}>Sold Out</span>}
              {item.imageUrl && (
                <img src={item.imageUrl.startsWith('http') ? item.imageUrl : `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}${item.imageUrl}`} alt={item.name} style={cardImageStyle} loading="lazy" decoding="async" width={300} height={160} />
              )}
              <h3 style={cardNameStyle}>{item.name}</h3>
              <div style={{ ...cardDescStyle, margin: 0 }} dangerouslySetInnerHTML={{ __html: item.description }} />
              <span style={priceStyle}>{item.caption ? item.caption : item.price != null ? formatPrice(item.price) : ''}</span>
              {item.sizes && <span style={metaStyle}>Sizes: {item.sizes}</span>}
              {item.quantity != null && <span style={metaStyle}>Qty: {item.quantity}</span>}
              <span style={metaStyle}>📍 {item.location}</span>
              {isOrganizer && (
                <div style={cardActions}>
                  <button style={iconBtn} onClick={() => openEditForm(item)} title="Edit" aria-label={`Edit ${item.name}`}>✏️</button>
                  <button style={iconBtn} onClick={() => handleDelete(item)} title="Delete" aria-label={`Delete ${item.name}`}>🗑️</button>
                </div>
              )}
            </div>
          ))}
          {/* Custom Marketplace Cards — tiles rendered like booklet sub-cards */}
          {cards.map((card) => (
            <div key={card.id} style={{ ...cardStyle, borderColor: PRIMARY, borderWidth: '2px' }}>
              <h3 style={{ ...cardNameStyle, textAlign: 'center' as const }}>{card.name}</h3>
              {card.description && <p style={{ ...cardDescStyle, textAlign: 'center' as const }}>{card.description}</p>}
              {card.imageUrl && (
                <img src={card.imageUrl.startsWith('http') ? card.imageUrl : `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}${card.imageUrl}`} alt={card.name} style={{ width: '100%', height: 'auto', maxHeight: '200px', objectFit: 'contain', borderRadius: '8px', marginBottom: '0.5rem', backgroundColor: '#f9fafb' }} loading="lazy" decoding="async" width={300} height={140} />
              )}
              {card.tiles.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginTop: '0.75rem' }}>
                  {card.tiles.map((tile) => (
                    <div key={tile.id} style={bookletSubCard}>
                      {tile.imageUrl ? (
                        <img src={tile.imageUrl.startsWith('http') ? tile.imageUrl : `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}${tile.imageUrl}`} alt={tile.label} style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px' }} loading="lazy" decoding="async" width={32} height={32} />
                      ) : (
                        <span style={{ fontSize: '1.5rem' }}>🏷️</span>
                      )}
                      <span style={bookletSubLabel}>{tile.label}</span>
                      {tile.price && <span style={{ fontSize: '0.6rem', color: PRIMARY, fontWeight: 600 }}>{tile.price}</span>}
                    </div>
                  ))}
                </div>
              )}
              {card.tiles.length === 0 && (
                <p style={{ textAlign: 'center', color: TEXT_MUTED, fontSize: '0.8rem', fontStyle: 'italic', marginTop: '0.5rem' }}>No tiles yet</p>
              )}
              {isOrganizer && (
                <div style={{ ...cardActions, justifyContent: 'center' }}>
                  <button style={iconBtn} onClick={() => { setSelectedCard(card); openAddTile(); }} title="Add Tile" aria-label={`Add tile to ${card.name}`}>➕</button>
                  <button style={iconBtn} onClick={() => openEditCard(card)} title="Edit Card" aria-label={`Edit ${card.name}`}>✏️</button>
                  <button style={iconBtn} onClick={() => handleDeleteCard(card)} title="Delete Card" aria-label={`Delete ${card.name}`}>🗑️</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div style={overlayStyle} onClick={() => setShowModal(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontFamily: FONT_HEADING, color: TEXT_DARK, marginTop: 0 }}>
              {editingItem ? 'Edit Item' : 'Add Item'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div style={fieldStyle}>
                <label style={labelStyle}>Name *</label>
                <input style={inputStyle} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Description *</label>
                <textarea style={textareaStyle} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Price</label>
                <input style={inputStyle} type="number" min="0" step="0.01" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} placeholder="Leave empty if no price" />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Caption <span style={{ fontWeight: 400, color: TEXT_MUTED, fontSize: '0.8rem' }}>(shown instead of price, e.g. "FREE", "Included")</span></label>
                <input style={inputStyle} value={formData.caption} onChange={(e) => setFormData({ ...formData, caption: e.target.value })} placeholder='e.g. FREE, Complimentary, Included' />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Image</label>
                {formData.imageUrl && <img src={formData.imageUrl.startsWith('http') ? formData.imageUrl : `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}${formData.imageUrl}`} alt="Preview" style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px', marginBottom: '4px' }} />}
                <input type="file" accept="image/*" style={{ fontSize: '0.85rem' }} onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    const data = await uploadFile<{ imageUrl: string }>('/marketplace/upload-image', file);
                    setFormData({ ...formData, imageUrl: data.imageUrl });
                  } catch { alert('Image upload failed.'); }
                }} />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Sizes</label>
                <input style={inputStyle} value={formData.sizes} onChange={(e) => setFormData({ ...formData, sizes: e.target.value })} placeholder="S,M,L,XL (optional)" />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Quantity</label>
                <input style={inputStyle} type="number" min="0" value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: e.target.value })} placeholder="Leave empty if unlimited" />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Location *</label>
                <input style={inputStyle} value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} placeholder="Registration Desk" required />
              </div>
              <div style={checkboxRowStyle}>
                <input type="checkbox" id="available" checked={formData.available} onChange={(e) => setFormData({ ...formData, available: e.target.checked })} />
                <label htmlFor="available" style={labelStyle}>Available</label>
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Sort Order</label>
                <input style={inputStyle} type="number" min="0" value={formData.sortOrder} onChange={(e) => setFormData({ ...formData, sortOrder: e.target.value })} />
              </div>
              <div style={modalFooter}>
                <button type="button" onClick={() => setShowModal(false)} style={{ ...addBtnStyle, backgroundColor: '#e0e0e0', color: '#333' }}>Cancel</button>
                <button type="submit" disabled={submitting} style={addBtnStyle}>{submitting ? 'Saving…' : (editingItem ? 'Update' : 'Create')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Memorial Tiles Preview Popup */}
      {showMemorialPopup && (
        <MemorialTilesPreviewPopup onClose={() => setShowMemorialPopup(false)} isOrganizer={!!isOrganizer} />
      )}

      {/* Advertisers Popup */}
      {showAdvertisersPopup && (
        <AdvertisersPreviewPopup onClose={() => setShowAdvertisersPopup(false)} isOrganizer={!!isOrganizer} />
      )}

      {/* Articles Preview Popup */}
      {showArticlesPopup && (
        <ArticlesPreviewPopup onClose={() => setShowArticlesPopup(false)} isOrganizer={!!isOrganizer} />
      )}

      {/* Sponsors Preview Popup */}
      {showSponsorsPopup && (
        <SponsorsPreviewPopup onClose={() => setShowSponsorsPopup(false)} />
      )}

      {/* Congrats Preview Popup */}
      {showCongratsPopup && (
        <CongratsPreviewPopup onClose={() => setShowCongratsPopup(false)} isOrganizer={!!isOrganizer} />
      )}

      {/* Custom Card Drill-down Popup */}
      {selectedCard && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '1rem' }} onClick={() => setSelectedCard(null)} role="dialog" aria-modal="true">
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', maxWidth: '560px', width: '100%', padding: '1.5rem', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', maxHeight: '85vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: TEXT_DARK, fontFamily: FONT_HEADING }}>{selectedCard.name}</h3>
              <button onClick={() => setSelectedCard(null)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#666' }} aria-label="Close">✕</button>
            </div>
            {selectedCard.imageUrl && (
              <img src={selectedCard.imageUrl.startsWith('http') ? selectedCard.imageUrl : `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}${selectedCard.imageUrl}`} alt={selectedCard.name} style={{ width: '100%', height: 'auto', maxHeight: '250px', objectFit: 'contain', borderRadius: '8px', marginBottom: '1rem', backgroundColor: '#f9fafb' }} loading="lazy" decoding="async" />
            )}
            {selectedCard.description && (
              <p style={{ color: TEXT_MUTED, fontSize: '0.9rem', marginBottom: '1rem', lineHeight: 1.5 }}>{selectedCard.description}</p>
            )}

            {isOrganizer && (
              <button onClick={openAddTile} style={{ ...addBtnStyle, fontSize: '0.8rem', padding: '0.4rem 1rem', marginBottom: '1rem' }}>+ Add Tile</button>
            )}

            {selectedCard.tiles.length === 0 ? (
              <p style={{ textAlign: 'center', color: TEXT_MUTED, fontStyle: 'italic', padding: '1rem 0' }}>No tiles yet.</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                {selectedCard.tiles.map((tile) => (
                  <div key={tile.id} style={{ padding: '0.75rem', backgroundColor: '#f9fafb', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '8px', position: 'relative' }}>
                    {tile.imageUrl && (
                      <img src={tile.imageUrl.startsWith('http') ? tile.imageUrl : `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}${tile.imageUrl}`} alt={tile.label} style={{ width: '100%', height: '80px', objectFit: 'cover', borderRadius: '4px', marginBottom: '0.5rem' }} />
                    )}
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: TEXT_DARK }}>{tile.label}</div>
                    {tile.description && <div style={{ fontSize: '0.75rem', color: TEXT_MUTED, marginTop: '2px' }}>{tile.description}</div>}
                    {tile.price && <div style={{ fontSize: '0.8rem', fontWeight: 700, color: PRIMARY, marginTop: '4px' }}>{tile.price}</div>}
                    {isOrganizer && (
                      <div style={{ display: 'flex', gap: '2px', marginTop: '6px' }}>
                        <button style={iconBtn} onClick={() => openEditTile(tile)} title="Edit Tile" aria-label={`Edit ${tile.label}`}>✏️</button>
                        <button style={iconBtn} onClick={() => handleDeleteTile(tile)} title="Delete Tile" aria-label={`Delete ${tile.label}`}>🗑️</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div style={{ marginTop: '1.25rem', textAlign: 'center' }}>
              <button onClick={() => setSelectedCard(null)} style={{ padding: '0.5rem 1.5rem', backgroundColor: PRIMARY, color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Card Create/Edit Modal */}
      {showCardModal && (
        <div style={{ ...overlayStyle, zIndex: 10002 }} onClick={() => setShowCardModal(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontFamily: FONT_HEADING, color: TEXT_DARK, marginTop: 0 }}>
              {editingCard ? 'Edit Card' : 'Add Card'}
            </h2>
            <form onSubmit={handleCardSubmit}>
              <div style={fieldStyle}>
                <label style={labelStyle}>Name *</label>
                <input style={inputStyle} value={cardForm.name} onChange={(e) => setCardForm({ ...cardForm, name: e.target.value })} required />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Description</label>
                <textarea style={textareaStyle} value={cardForm.description} onChange={(e) => setCardForm({ ...cardForm, description: e.target.value })} />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Image</label>
                {cardForm.imageUrl && <img src={cardForm.imageUrl.startsWith('http') ? cardForm.imageUrl : `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}${cardForm.imageUrl}`} alt="Preview" style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px', marginBottom: '4px' }} />}
                <input type="file" accept="image/*" style={{ fontSize: '0.85rem' }} onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    const data = await uploadFile<{ imageUrl: string }>('/marketplace/upload-image', file);
                    setCardForm({ ...cardForm, imageUrl: data.imageUrl });
                  } catch { alert('Image upload failed.'); }
                }} />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Sort Order</label>
                <input style={inputStyle} type="number" min="0" value={cardForm.sortOrder} onChange={(e) => setCardForm({ ...cardForm, sortOrder: e.target.value })} />
              </div>
              <div style={modalFooter}>
                <button type="button" onClick={() => setShowCardModal(false)} style={{ ...addBtnStyle, backgroundColor: '#e0e0e0', color: '#333' }}>Cancel</button>
                <button type="submit" disabled={cardSubmitting} style={addBtnStyle}>{cardSubmitting ? 'Saving…' : (editingCard ? 'Update' : 'Create')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tile Create/Edit Modal */}
      {showTileModal && (
        <div style={{ ...overlayStyle, zIndex: 10002 }} onClick={() => setShowTileModal(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontFamily: FONT_HEADING, color: TEXT_DARK, marginTop: 0 }}>
              {editingTile ? 'Edit Tile' : 'Add Tile'}
            </h2>
            <form onSubmit={handleTileSubmit}>
              <div style={fieldStyle}>
                <label style={labelStyle}>Label *</label>
                <input style={inputStyle} value={tileForm.label} onChange={(e) => setTileForm({ ...tileForm, label: e.target.value })} required />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Description</label>
                <textarea style={textareaStyle} value={tileForm.description} onChange={(e) => setTileForm({ ...tileForm, description: e.target.value })} />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Price</label>
                <input style={inputStyle} value={tileForm.price} onChange={(e) => setTileForm({ ...tileForm, price: e.target.value })} placeholder="$25.00" />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Image</label>
                {tileForm.imageUrl && <img src={tileForm.imageUrl.startsWith('http') ? tileForm.imageUrl : `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}${tileForm.imageUrl}`} alt="Preview" style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px', marginBottom: '4px' }} />}
                <input type="file" accept="image/*" style={{ fontSize: '0.85rem' }} onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    const data = await uploadFile<{ imageUrl: string }>('/marketplace/upload-image', file);
                    setTileForm({ ...tileForm, imageUrl: data.imageUrl });
                  } catch { alert('Image upload failed.'); }
                }} />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Sort Order</label>
                <input style={inputStyle} type="number" min="0" value={tileForm.sortOrder} onChange={(e) => setTileForm({ ...tileForm, sortOrder: e.target.value })} />
              </div>
              <div style={modalFooter}>
                <button type="button" onClick={() => setShowTileModal(false)} style={{ ...addBtnStyle, backgroundColor: '#e0e0e0', color: '#333' }}>Cancel</button>
                <button type="submit" disabled={cardSubmitting} style={addBtnStyle}>{cardSubmitting ? 'Saving…' : (editingTile ? 'Update' : 'Create')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cover Image Popup */}
      {showCoverPopup && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '1rem' }}
          onClick={() => setShowCoverPopup(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Booklet Cover Preview"
        >
          <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
            <img src="/images/booklet-cover.jpg" alt="Weekend Booklet Cover" style={{ maxWidth: '100%', maxHeight: '85vh', borderRadius: '8px', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }} />
            <button
              onClick={() => setShowCoverPopup(false)}
              style={{ position: 'absolute', top: '-12px', right: '-12px', width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#fff', border: 'none', fontSize: '1.1rem', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              aria-label="Close cover preview"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
